import * as React from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Banner } from "@/components/ui/banner";
import { FormField } from "@/components/ui/form-field";
import { Input } from "@/components/ui/input";
import { ClientNameReveal } from "@/components/clients/ClientNameReveal";
import { OfflineListNotice } from "@/components/pwa/OfflineListNotice";
import {
  useListScoringResults,
  useResultsThisWeekCount,
} from "@/hooks/queries/useListScoringResults";
import { useListClients } from "@/hooks/queries/useListClients";
import { isOfflineError } from "@/lib/api-client";
import { formatShortDate } from "@/lib/format-date";
import { formatStandardScore } from "@/routes/evaluation/results/results-format";
import type { ScoringResultListItem } from "@/hooks/queries/scoring-types";
import type { ClientRecord } from "@/hooks/queries/client-types";

// Results library (UX Screen 13, Story 9.13). The clinician's searchable library of
// completed AMPS results, consuming the Story 9.14 list endpoint via
// `useListScoringResults` and opening each row into the Results panel (Story 9.7,
// Screen 08) at `/evaluations/{evaluation_id}`. Pure frontend — no new query, no backend.
//
// The Client column reuses the shared per-row `ClientNameReveal` (names are PHI, NEVER
// bulk-decrypted — the mockup's global privacy toggle is deliberately replaced by the
// audited per-row reveal, 5.4/6.9). Search is client-side over the loaded page only (no
// server text search exists — FR12-deferred, evaluations-list precedent). Standard scores
// render verbatim (Decimal-string precision, never float re-rounded); a NULL score is the
// out-of-normative-range marker → em dash, never 0. Tokens only, no pure-white background,
// `tabular-nums` on the numeric/date columns.

const PAGE_LIMIT = 20;
// A best-effort single page of clients to resolve the Client column's up-front erased
// (GDPR tombstone) state. A client beyond this page falls back to UUID-only until revealed;
// Reveal still returns 410/ADMIN-004 and latches the tombstone on click, so this never
// discloses PHI (mirrors EvaluationsListScreen's accepted, frontend-only join gap).
const CLIENT_JOIN_LIMIT = 100;

export function ResultsLibraryScreen() {
  const [page, setPage] = React.useState(1);
  const [search, setSearch] = React.useState("");
  // Revealed names mirrored here transiently so the client-side search can match them
  // (display still lives in each cell). Never persisted, never logged (PHI).
  const [revealedNames, setRevealedNames] = React.useState<Record<string, string>>({});

  const listQuery = useListScoringResults({ page, limit: PAGE_LIMIT });
  const { data, isPending, isError, error, refetch, isFetching } = listQuery;

  // KPI probes — each a cheap, PAGE-INDEPENDENT count read (PR #557 review). "Results this
  // week" = limit=1 + date_from; "Completed results" = limit=1 (the unconditional total).
  // Deliberately NOT read from `listQuery.data.count`: that query is keyed by `page`, so a
  // page-≥2 load/error would flip the tile to "—" even though the total was known on page 1
  // — and it would silently track any future filter added to the main list.
  const resultsThisWeek = useResultsThisWeekCount();
  const completedCount = useListScoringResults({ limit: 1 });

  // Client join map (id → record) for the Client column's up-front erased handling.
  const clientsQuery = useListClients({ limit: CLIENT_JOIN_LIMIT });
  const clientsById = React.useMemo(() => {
    const map = new Map<string, ClientRecord>();
    for (const c of clientsQuery.data?.results ?? []) map.set(c.id, c);
    return map;
  }, [clientsQuery.data]);

  const offline = isOfflineError(error);
  const count = data?.count ?? 0;
  const totalPages = count > 0 ? Math.ceil(count / PAGE_LIMIT) : 1;
  const rows = data?.results ?? [];

  // Clamp to a valid page once settled data lands (mirrors the sibling lists).
  React.useEffect(() => {
    if (data && page > totalPages) setPage(totalPages);
  }, [data, page, totalPages]);

  // Client-side search over the LOADED page: by client UUID and by any revealed name.
  const query = search.trim().toLowerCase();
  const filteredRows = query
    ? rows.filter((row) => {
        if (row.client_id.toLowerCase().includes(query)) return true;
        const name = revealedNames[row.client_id];
        return name ? name.toLowerCase().includes(query) : false;
      })
    : rows;

  const onRevealed = React.useCallback(
    (clientId: string, name: { first: string; last: string }) => {
      setRevealedNames((prev) => ({ ...prev, [clientId]: `${name.first} ${name.last}` }));
    },
    [],
  );

  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="display-xs text-[color:var(--text-primary)]">Results library</h1>
        <p className="text-sm text-[color:var(--text-tertiary)]">
          Review recent AMPS outputs and open the detailed in-app results view for each completed
          evaluation.
        </p>
      </header>

      {/* Two KPI tiles (Screen 13). */}
      <div
        className="grid grid-cols-1 gap-4 sm:grid-cols-2"
        role="group"
        aria-label="Results summary"
      >
        <KpiTile label="Results this week" query={resultsThisWeek} />
        <KpiTile label="Completed results" query={completedCount} />
      </div>

      {/* Client-side search (by client UUID and any revealed name — no server text search). */}
      <FormField
        label="Search by client / client ID"
        htmlFor="results-search"
        helper="Searches the current page by client ID and any names you've revealed."
      >
        <div className="relative">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--text-placeholder)]"
          />
          <Input
            id="results-search"
            type="search"
            placeholder="Search this page"
            className="max-w-[420px] pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </FormField>

      {/* "Results list" section head + subtitle (Screen 13 field contract). */}
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">Results list</h2>
        <p className="text-sm text-[color:var(--text-tertiary)]">
          Open detailed result views for each completed client evaluation.
        </p>
      </div>

      {/* Body: loading / offline / error / empty / no-match / table. */}
      {isPending ? (
        <div
          className="flex items-center justify-center rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-6 py-12 text-sm text-[color:var(--text-tertiary)]"
          aria-busy="true"
        >
          Loading results…
        </div>
      ) : offline ? (
        // Online-only list: an honest offline notice, never a false "No results yet."
        // Keep any stale cached rows visible UNDER the notice rather than blanking them.
        rows.length > 0 ? (
          <>
            <OfflineListNotice message="You're offline — showing the last loaded results." />
            <ResultsTable rows={filteredRows} clientsById={clientsById} onRevealed={onRevealed} />
          </>
        ) : (
          <OfflineListNotice />
        )
      ) : isError ? (
        <Banner variant="danger" title="Couldn't load your results">
          <div className="flex flex-col gap-2">
            <span>We couldn't load your results. Please try again.</span>
            <span>
              <Button variant="secondary" size="sm" onClick={() => void refetch()}>
                Try again
              </Button>
            </span>
          </div>
        </Banner>
      ) : count === 0 ? (
        <EmptyState />
      ) : filteredRows.length === 0 ? (
        <div className="rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-6 py-12 text-center text-sm text-[color:var(--text-tertiary)]">
          No results on this page match your search.
        </div>
      ) : (
        <>
          <ResultsTable rows={filteredRows} clientsById={clientsById} onRevealed={onRevealed} />
          <Pagination
            page={page}
            totalPages={totalPages}
            hasPrevious={Boolean(data?.previous)}
            hasNext={Boolean(data?.next)}
            isFetching={isFetching}
            onPrevious={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => p + 1)}
          />
        </>
      )}
    </section>
  );
}

function KpiTile({
  label,
  query,
}: {
  label: string;
  query: ReturnType<typeof useListScoringResults>;
}) {
  // "—" (not a false 0) until a load has succeeded — so a failed/pending count never reads
  // as zero. After a success, a transient refetch error keeps the last good count.
  const value = query.data ? query.data.count : "—";
  return (
    <div className="rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-5 py-4">
      <div className="text-sm text-[color:var(--text-tertiary)]">{label}</div>
      <div className="tabular-nums text-2xl font-semibold text-[color:var(--text-primary)]">
        {value}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center gap-3 rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-6 py-12 text-center">
      <p className="text-md text-[color:var(--text-secondary)]">
        No results yet — completed evaluations will appear here once they are scored.
      </p>
    </div>
  );
}

function ResultsTable({
  rows,
  clientsById,
  onRevealed,
}: {
  rows: ScoringResultListItem[];
  clientsById: Map<string, ClientRecord>;
  onRevealed: (clientId: string, name: { first: string; last: string }) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)]">
      <table className="w-full border-collapse text-left text-sm">
        <caption className="sr-only">Your completed results</caption>
        <thead>
          <tr className="border-b border-[color:var(--bg-quaternary)] text-xs uppercase tracking-wide text-[color:var(--text-tertiary)]">
            <th scope="col" className="px-4 py-3 font-semibold">Client</th>
            <th scope="col" className="px-4 py-3 font-semibold">Date</th>
            <th scope="col" className="px-4 py-3 font-semibold">Motor / Process</th>
            <th scope="col" className="px-4 py-3 font-semibold">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <ResultRow
              key={row.id}
              row={row}
              client={clientsById.get(row.client_id)}
              onRevealed={onRevealed}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ResultRow({
  row,
  client,
  onRevealed,
}: {
  row: ScoringResultListItem;
  client: ClientRecord | undefined;
  onRevealed: (clientId: string, name: { first: string; last: string }) => void;
}) {
  return (
    <tr className="border-b border-[color:var(--bg-quaternary)] last:border-b-0 align-top">
      <td className="px-4 py-3">
        <div className="flex flex-col gap-1">
          <ClientNameReveal
            clientId={row.client_id}
            initialErased={Boolean(client?.gdpr_erased_at)}
            erasedAt={client?.gdpr_erased_at ?? null}
            onRevealed={(name) => onRevealed(row.client_id, name)}
          />
          {/* Secondary identifier is the system Client ID (UUID) — the mock's
              "CITS-######" display code is FR12-deferred and intentionally not shown. */}
          <span
            className="tabular-nums text-xs text-[color:var(--text-tertiary)]"
            title={row.client_id}
          >
            Client ID: {row.client_id}
          </span>
        </div>
      </td>
      <td className="px-4 py-3 align-middle tabular-nums text-[color:var(--text-secondary)]">
        {formatShortDate(row.created_at)}
      </td>
      {/* Standard scores rendered verbatim at stored precision; NULL = out-of-normative-range
          → em dash per side, never 0. */}
      <td className="px-4 py-3 align-middle tabular-nums text-[color:var(--text-primary)]">
        {formatStandardScore(row.std_motor)} / {formatStandardScore(row.std_process)}
      </td>
      <td className="px-4 py-3 align-middle">
        <Button asChild variant="secondary" size="sm">
          <Link to={`/evaluations/${row.evaluation_id}`}>Open</Link>
        </Button>
      </td>
    </tr>
  );
}

function Pagination({
  page,
  totalPages,
  hasPrevious,
  hasNext,
  isFetching,
  onPrevious,
  onNext,
}: {
  page: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
  isFetching: boolean;
  onPrevious: () => void;
  onNext: () => void;
}) {
  return (
    <nav className="flex items-center justify-between gap-4" aria-label="Pagination">
      <span className="tabular-nums text-sm text-[color:var(--text-tertiary)]">
        Page {page} of {totalPages}
      </span>
      <div className="flex items-center gap-2">
        <Button variant="secondary" size="sm" onClick={onPrevious} disabled={!hasPrevious || isFetching}>
          <ChevronLeft aria-hidden="true" className="h-4 w-4" />
          Previous
        </Button>
        <Button variant="secondary" size="sm" onClick={onNext} disabled={!hasNext || isFetching}>
          Next
          <ChevronRight aria-hidden="true" className="h-4 w-4" />
        </Button>
      </div>
    </nav>
  );
}

export default ResultsLibraryScreen;
